import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Color from '@tiptap/extension-color'
import FontFamily from '@tiptap/extension-font-family'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold as BoldIcon,
  CaseSensitive,
  Eraser,
  Italic as ItalicIcon,
  List,
  ListOrdered,
  RotateCcw,
  RotateCw,
  Underline as UnderlineIcon,
} from 'lucide-react'

export default function RichTextEditor({ value, onChange, placeholder, onRegister, onFocus }) {
  const debounceRef = useRef(null)
  const [currentFontSize, setCurrentFontSize] = useState('')
  const lastRangeRef = useRef({ from: 0, to: 0 })

  const extensions = useMemo(() => [
    StarterKit.configure({ history: true }),
    Underline,
    TextStyle,
    Color.configure({ types: ['textStyle'] }),
    FontFamily,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Placeholder.configure({ placeholder }),
  ], [placeholder])

  const editor = useEditor({
    extensions,
    autofocus: false,
    onUpdate: ({ editor }) => {
      requestAnimationFrame(() => {
        try { editor.commands.focus() } catch {}
      })
      const html = editor.getHTML()
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => onChange(html), 0)
    },
    editorProps: {
      attributes: { class: 'outline-none', spellcheck: 'false' },
      handleDOMEvents: {
        focus: () => {
          try { onFocus && onFocus() } catch {}
          return false
        },
      },
    },
  })

  useEffect(() => {
    if (!editor) return
    if (editor.isFocused) return
    const current = editor.getHTML()
    const incoming = value || ''
    if (incoming !== current) editor.commands.setContent(incoming, false)
  }, [value, editor])

  useEffect(() => {
    if (!editor || !onRegister) return
    const api = { insertText: (text) => editor.chain().focus().insertContent(text).run() }
    onRegister(api)
  }, [editor, onRegister])

  useEffect(() => {
    if (!editor) return
    const handler = () => {
      const sel = editor.state.selection
      lastRangeRef.current = { from: sel.from, to: sel.to }
      let fs = editor.getAttributes('textStyle')?.fontSize || ''
      if (!fs) {
        try {
          const basePx = window.getComputedStyle(editor.view.dom).fontSize
          const n0 = parseInt(String(basePx).replace('px', ''), 10)
          setCurrentFontSize(Number.isFinite(n0) ? String(n0) : '')
        } catch {
          setCurrentFontSize('')
        }
        return
      }
      const n = parseInt(String(fs).replace('px', ''), 10)
      setCurrentFontSize(Number.isFinite(n) ? String(n) : '')
    }
    handler()
    editor.on('selectionUpdate', handler)
    editor.on('transaction', handler)
    return () => {
      editor.off('selectionUpdate', handler)
      editor.off('transaction', handler)
    }
  }, [editor])

  const applyFontSize = (px) => {
    if (!editor) return
    const { from, to } = lastRangeRef.current || { from: undefined, to: undefined }
    const chain = editor.chain().focus()
    if (Number.isFinite(from) && Number.isFinite(to)) chain.setTextSelection({ from, to })
    chain.setMark('textStyle', { fontSize: `${px}px` }).run()
    const transaction = editor.state.tr
    editor.view.dispatch(transaction)
    setCurrentFontSize(String(px))
  }

  const clearFontSize = () => {
    if (!editor) return
    const { from, to } = lastRangeRef.current || { from: undefined, to: undefined }
    const attrs = editor.getAttributes('textStyle') || {}
    const { fontSize, ...keep } = attrs
    const chain = editor.chain().focus()
    if (Number.isFinite(from) && Number.isFinite(to)) chain.setTextSelection({ from, to })
    chain.unsetMark('textStyle').run()
    if (Object.keys(keep).length) {
      const ch2 = editor.chain().focus()
      if (Number.isFinite(from) && Number.isFinite(to)) ch2.setTextSelection({ from, to })
      ch2.setMark('textStyle', keep).run()
    }
  }

  const handleCustomSizeKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      const px = Number(e.currentTarget.value)
      if (px) applyFontSize(px)
    }
  }

  const toggleCase = () => {
    if (!editor) return
    const { from, to } = lastRangeRef.current || { from: undefined, to: undefined }
    if (!Number.isFinite(from) || !Number.isFinite(to)) return

    const { state } = editor
    const { doc } = state
    let text = ''
    let hasUpper = false

    doc.nodesBetween(from, to, (node, pos) => {
      if (node.isText) {
        const nodeText = node.text
        const start = Math.max(0, from - pos)
        const end = Math.min(nodeText.length, to - pos)
        text += nodeText.slice(start, end)
        if (/[A-Z]/.test(nodeText.slice(start, end))) {
          hasUpper = true
        }
      }
    })

    if (!text) return

    const transformed = text
      .replace(/\{\{[^}]+\}\}/g, (match) => match)
      .replace(/[a-zA-ZÀ-ÿ]/g, (char) => (hasUpper ? char.toLowerCase() : char.toUpperCase()))

    editor.chain()
      .focus()
      .setTextSelection({ from, to })
      .insertContent(transformed)
      .run()
  }

  const handleDrop = (e) => {
    if (!editor) return
    e.preventDefault()
    const text = e.dataTransfer.getData('text/plain')
    if (!text) return
    const pos = editor.view.posAtCoords({ left: e.clientX, top: e.clientY })
    if (pos) editor.chain().focus().insertContentAt(pos.pos, text).run()
    else editor.chain().focus().insertContent(text).run()
  }

  return (
    <div className="bg-slate-950/50 border border-slate-800 rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-800 bg-slate-950/40">
        <button title="Negrito" aria-label="Negrito" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor?.chain().focus().toggleBold().run()} className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white">
          <BoldIcon size={18} />
        </button>
        <button title="Itálico" aria-label="Itálico" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor?.chain().focus().toggleItalic().run()} className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white">
          <ItalicIcon size={18} />
        </button>
        <button title="Sublinhado" aria-label="Sublinhado" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor?.chain().focus().toggleMark('underline').run()} className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white">
          <UnderlineIcon size={18} />
        </button>
        <button title="Alternar MAIÚSC/minúsculas" aria-label="Alternar MAIÚSC/minúsculas" type="button" onMouseDown={(e) => e.preventDefault()} onClick={toggleCase} className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white">
          <CaseSensitive size={18} />
        </button>
        <div className="w-px h-8 bg-slate-800 mx-1" />
        <select
          title="Tamanho da fonte"
          value={currentFontSize || ''}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation() } }}
          onChange={(e) => {
            const val = e.target.value
            setCurrentFontSize(val)
            const px = Number(val)
            if (px) applyFontSize(px)
          }}
          className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none"
        >
          <option value="" disabled>Tamanho</option>
          <option value="12">12</option>
          <option value="14">14</option>
          <option value="16">16</option>
          <option value="18">18</option>
          <option value="24">24</option>
          <option value="32">32</option>
          {currentFontSize && !['12', '14', '16', '18', '24', '32'].includes(currentFontSize) && (
            <option value={currentFontSize}>{currentFontSize}</option>
          )}
        </select>
        <input
          title="Tamanho personalizado"
          type="number"
          min="8"
          max="96"
          value={currentFontSize || ''}
          onChange={(e) => setCurrentFontSize(e.target.value)}
          onBlur={(e) => { const px = Number(e.target.value); if (px) applyFontSize(px) }}
          onKeyDown={handleCustomSizeKey}
          className="w-20 bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none"
          placeholder="px"
        />
        <button
          title="Limpar tamanho (padrão)"
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { clearFontSize() }}
          className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white"
        >
          <Eraser size={18} />
        </button>

        <select title="Fonte" defaultValue="" onChange={(e) => { const ff = e.target.value; if (ff) { const { from, to } = lastRangeRef.current || { from: undefined, to: undefined }; const chain = editor?.chain().focus(); if (chain) { if (Number.isFinite(from) && Number.isFinite(to)) chain.setTextSelection({ from, to }); chain.setFontFamily(ff).run() } } }} className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none">
          <option value="" disabled>Fonte</option>
          <option value="Inter, system-ui, sans-serif">Inter</option>
          <option value="Arial, Helvetica, sans-serif">Arial</option>
          <option value="Georgia, serif">Georgia</option>
          <option value="'Times New Roman', Times, serif">Times New Roman</option>
          <option value="'Courier New', Courier, monospace">Courier New</option>
        </select>

        <label title="Cor do texto" className="px-2 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-white text-sm flex items-center gap-2">
          <span className="text-xs">Cor</span>
          <input type="color" onChange={(e) => { const { from, to } = lastRangeRef.current || { from: undefined, to: undefined }; const chain = editor?.chain().focus(); if (chain) { if (Number.isFinite(from) && Number.isFinite(to)) chain.setTextSelection({ from, to }); chain.setColor(e.target.value).run() } }} className="w-8 h-7 bg-transparent border-0 p-0 cursor-pointer" />
        </label>
        <div className="w-px h-8 bg-slate-800 mx-1" />
        <button title="Alinhar à esquerda" aria-label="Alinhar à esquerda" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor?.chain().focus().setTextAlign('left').run()} className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white">
          <AlignLeft size={18} />
        </button>
        <button title="Centralizar" aria-label="Centralizar" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor?.chain().focus().setTextAlign('center').run()} className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white">
          <AlignCenter size={18} />
        </button>
        <button title="Alinhar à direita" aria-label="Alinhar à direita" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor?.chain().focus().setTextAlign('right').run()} className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white">
          <AlignRight size={18} />
        </button>
        <button title="Justificar" aria-label="Justificar" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor?.chain().focus().setTextAlign('justify').run()} className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white">
          <AlignJustify size={18} />
        </button>
        <div className="w-px h-8 bg-slate-800 mx-1" />
        <button title="Lista com marcadores" aria-label="Lista com marcadores" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor?.chain().focus().toggleBulletList().run()} className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white">
          <List size={18} />
        </button>
        <button title="Lista numerada" aria-label="Lista numerada" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor?.chain().focus().toggleOrderedList().run()} className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white">
          <ListOrdered size={18} />
        </button>
        <button title="Desfazer" aria-label="Desfazer" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor?.chain().focus().undo().run()} className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white">
          <RotateCcw size={18} />
        </button>
        <button title="Refazer" aria-label="Refazer" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor?.chain().focus().redo().run()} className="px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white">
          <RotateCw size={18} />
        </button>
      </div>
      <div className="w-full min-h-[260px] p-4 text-white text-sm leading-relaxed custom-scrollbar overflow-y-auto">
        <EditorContent editor={editor} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} className="outline-none" />
      </div>
    </div>
  )
}
